// Round controller — extracted verbatim from GameScene.ts (FACE&FURY S2 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were
// rewritten to `export function name(this: GameScene, ...)` and bound back onto the scene as fields.
import Phaser from 'phaser';
import { type Snapshot, DAILY_MUTATORS } from '../../core';
import { PALETTE, hex, FONT } from '../keys';
import { audio } from '../audio';
import { motionAllowed } from '../settings';
import { isLandscape } from '../stage';
import { shake } from '../juice';
import { attachMascot } from '../mascot';
import { noviceCoachForBoard } from '../noviceRun';
import { matchWorldBucket } from './boardChrome';
import { PLAYER, compactLabel } from './types';
import type { GameScene } from '../scenes/GameScene';

// ── S9 LANE-G1 GET-READY CONFIG ────────────────────────────────────────────────────────────────
/** #22: the GET READY veil is the FIRST menu→game beat — bump the scrim so the bright rival/human
 *  tokens don't peek through behind the topic card (audit: "board under-dimmed, rival peeks out").
 *  It stays the transparent input-blocker (the plate re-states the topic + rule on top, fully legible). */
const GET_READY_SCRIM = 0.78;
/** S9 FIX-A: the topic plate re-states the rule over the board. Keep it near-solid so tile labels do
 *  not ghost through the rule/start copy at 150% text scale, while still letting a hint of world tint read. */
const GET_READY_PLATE_ALPHA = 0.985;

export function openPreRoundCoach(this: GameScene, snap: Snapshot): void {
    if (this.preRoundActive) return;
    this.preRoundActive = true;
    this.cancelPendingSteps(); // a coach card interrupting play drops any in-flight glide.
    const W = this.scale.width;
    const H = this.scale.height;
    const layer = this.add.container(0, 0).setDepth(26_000);
    // "Get ready" beat: the board + rule read behind a dimming input-blocker (#22 bumped the scrim so
    // the bright tokens don't peek through the topic card) — the plate re-states the topic + rule on top
    // so nothing load-bearing is lost. The player gets a beat to read before the timer runs. Auto-starts
    // (never a wall for returning players); tap anywhere to start now. Replaces the old centre modal
    // that re-taught the first-run carousel and covered the very board it described — teaching now
    // lives in the rule banner + the just-in-time coaching on the first wrong-eat / first arm.
    // A darker veil reads as a deliberate "ready" dim and keeps the topic/rule legible over the board.
    const veil = this.add.rectangle(W / 2, H / 2, W, H, PALETTE.bg, GET_READY_SCRIM).setInteractive({ useHandCursor: true });
    const readyY = H * 0.4;
    // Daily twist eyebrow: the mutator IS the day's identity — lead with it over a generic GET
    // READY. The plate grows 12% on daily to fit the extra blurb line without crowding the topic.
    // Reads the create()-resolved id (NOT mutatorForDay()) so a deep-linked twist shows correctly.
    const twist = this.dailyMutatorId >= 0 ? DAILY_MUTATORS[this.dailyMutatorId]! : null;
    const plateH = Math.max(124, H * 0.18) * (twist ? 1.12 : 1);
    const plateW = Math.min(W * 0.86, 480);
    // S6: the pre-round card picks up the topic-world bucket — the plate border + eyebrow + topic glow
    // tint to the bucket accent, and a small "<BUCKET> WORLD" badge rides the plate top. The big TOPIC
    // text stays scan-cyan (legibility + it never collides with a LOCKED gameplay-state accent).
    const worldBucket = this.boardWorldBucket();
    const worldAccent = this.boardWorldAccent();
    const plate = this.add.rectangle(W / 2, readyY, plateW, plateH, PALETTE.surface, GET_READY_PLATE_ALPHA)
      .setStrokeStyle(2, worldAccent, 0.75);
    const noviceCoach = this.noviceRun ? noviceCoachForBoard(this.boardIndex) : null;
    const topicText = compactLabel(this.topicLabel || snap.topic || this.topic || 'Trivia', 30);
    // Topic-forward: demote "GET READY" to a small eyebrow so the TOPIC is unmistakably the hero of the
    // pre-round flash — the player's one beat to register what this board is about (and the clean topic
    // card reads well in social-replay clips).
    const ready = this.add
      .text(W / 2, readyY - plateH * 0.36, noviceCoach?.title ?? (twist ? `TODAY'S TWIST: ${twist.name}` : this.rivalWarmup ? 'WARM UP' : 'GET READY'), {
        fontFamily: FONT.display,
        fontSize: `${Math.max(13, Math.min(20, W * 0.04))}px`,
        color: hex(worldAccent),
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    const topic = this.add
      .text(W / 2, readyY - plateH * 0.02, topicText.toUpperCase(), {
        fontFamily: FONT.display,
        fontSize: `${Math.max(26, Math.min(48, W * 0.09))}px`,
        color: hex(PALETTE.scan),
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: Math.min(W * 0.78, 430), useAdvancedWrap: true },
      })
      .setOrigin(0.5)
      // Topic halo tints to the world accent (a glow behind the text never reads as a state signal).
      .setShadow(0, 0, hex(worldAccent), 12, false, true);
    // Small bucket badge — the "label treatment" that names the world riding the plate's top edge.
    // S9 FIX-C (world-pill honesty): render the pill ONLY when the topic GENUINELY maps to a bucket.
    // The world backdrop/accent keep the documented science fallback (a neutral default LOOK is fine),
    // but a wrong LABEL — "SCIENCE WORLD" stamped on a fruit board — is an honesty bug. Unmapped topics
    // simply get no pill; the bucket map itself is deliberately NOT grown here.
    const worldMatched = matchWorldBucket(`${this.topic ?? ''} ${this.topicLabel ?? ''}`) !== undefined;
    const badgeY = readyY - plateH / 2;
    let badgeBottom = badgeY; // plate-top anchor when no pill renders
    let badgeText: Phaser.GameObjects.Text | null = null;
    let badgeG: Phaser.GameObjects.Graphics | null = null;
    if (worldMatched) {
      badgeText = this.add
        .text(0, 0, `${worldBucket.toUpperCase()} WORLD`, {
          fontFamily: FONT.display,
          fontSize: `${Math.max(9, Math.min(12, W * 0.024))}px`,
          color: hex(worldAccent),
          fontStyle: '700',
        })
        .setOrigin(0.5);
      badgeG = this.add.graphics();
      const bw = badgeText.width + 16;
      const bh = badgeText.height + 7;
      badgeG.fillStyle(PALETTE.void, 0.92);
      badgeG.fillRoundedRect(W / 2 - bw / 2, badgeY - bh / 2, bw, bh, bh / 2);
      badgeG.lineStyle(1.25, worldAccent, 0.9);
      badgeG.strokeRoundedRect(W / 2 - bw / 2, badgeY - bh / 2, bw, bh, bh / 2);
      badgeText.setPosition(W / 2, badgeY);
      badgeBottom = badgeY + bh / 2;
    }
    // S9 FIX-C (G1 AMATEUR 4 — GET READY kicker occluded on desktop, default + ts150): the kicker and a
    // 2-line TOPIC were both placed at fixed plate FRACTIONS, so a wrapped topic's top rode up through
    // "GET READY" (and the kicker grazed the world pill). Flow the stack instead: the kicker clears the
    // pill / plate top, the topic clears the kicker's REAL rendered bottom (+ its glow), and the existing
    // hint offset + enclosing-plate growth below absorb any shift by growing the plate DOWNWARD (the
    // badge stays glued to the fixed top edge). One-line topics keep their original centred position
    // (Math.max no-ops). reduceMotion takes the same static flow.
    ready.setY(Math.max(ready.y, badgeBottom + 7 + ready.height / 2));
    topic.setY(Math.max(topic.y, ready.y + ready.height / 2 + 14 + topic.height / 2));
    const hintText =
      `${twist ? `${twist.blurb}\n` : ''}${noviceCoach?.hint ?? `Rule: ${compactLabel(snap.rule, 68)}`}\n${
        this.challengeTarget > 0
          ? `Friend target: ${this.challengeTarget} - tap to start`
          : this.aiEnabled && !this.rivalWarmup
            ? 'tap to start - outscore BYT3 with correct answers'
            : 'tap to start'
      }`;
    const hint = this.add
      .text(W / 2, readyY + plateH * 0.31, hintText, {
        fontFamily: FONT.family,
        fontSize: `${Math.max(12, W * 0.026)}px`,
        color: hex(PALETTE.textDim),
        align: 'center',
        wordWrap: { width: Math.min(W * 0.82, 420), useAdvancedWrap: true },
      })
      .setOrigin(0.5);
    // #23: keep the rule subtext clear of the TITLE and let it arrive as its OWN beat. A long (2-line)
    // topic otherwise overlaps the hint during the slide-in — offset the hint below the topic's REAL
    // rendered bottom, and fade it in only AFTER the title lands. reduceMotion: everything is placed
    // statically at full alpha (still non-overlapping), no slide / no pulse.
    const topicBottom = topic.y + topic.height / 2;
    hint.setY(Math.max(hint.y, topicBottom + 12 + hint.height / 2));
    // #3 (S9 FIX-A): a long (2-line) topic pushes the rule subtext BELOW the original plate, stranding
    // the hint over the bare (only-scrimmed) board where the tile labels ghost through it. The plate is
    // meant to BACK the topic + rule together (see the veil comment above), so GROW it to ENCLOSE the
    // hint — keep the top edge fixed (the world badge rides it) and extend the bottom past the hint.
    // Together with the 0.96 plate alpha the rule subtext now always sits on a solid card, never on
    // ghosting labels. Runs before the motion block, so reduceMotion gets the same enclosing plate.
    const plateTop = readyY - plateH / 2;
    const hintBottom = hint.y + hint.height / 2 + 14;
    if (hintBottom > readyY + plateH / 2) {
      plate.setSize(plateW, hintBottom - plateTop);
      plate.setY((plateTop + hintBottom) / 2);
    }
    if (motionAllowed()) {
      this.tweens.add({ targets: ready, scale: { from: 0.7, to: 1 }, duration: 340, ease: 'Back.easeOut' });
      this.tweens.add({ targets: topic, scale: { from: 0.86, to: 1 }, duration: 420, ease: 'Back.easeOut', delay: 60 });
      // The rule/tap copy must be readable on the first captured frame; earlier builds started it at
      // alpha 0 and left desktop coach cards looking blank. Keep it present, then let it breathe after
      // the title settles.
      hint.setAlpha(0.82);
      this.tweens.add({
        targets: hint,
        alpha: 1,
        duration: 260,
        delay: 420,
        ease: 'Quad.easeOut',
        onComplete: () => {
          if (hint.active) {
            this.tweens.add({ targets: hint, alpha: { from: 1, to: 0.4 }, duration: 720, yoyo: true, repeat: -1 });
          }
        },
      });
    }
    veil.on('pointerup', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev?: Phaser.Types.Input.EventData) => {
      ev?.stopPropagation?.();
      this.startRound();
    });
    layer.add([veil, plate, ...(badgeG && badgeText ? [badgeG, badgeText] : []), ready, topic, hint]);
    layer.setAlpha(0);
    this.tweens.add({ targets: layer, alpha: 1, duration: 160, ease: 'Quad.easeOut' });
    this.preRoundLayer = layer;
    // 9+ punch #19 — RIVAL INCOMING: on BYT3's join board (novice board 2, the coach copy already reads
    // "RIVAL INCOMING") slam the rival CREATURE into frame over the plate's top-right corner with a small
    // impact shake + a heavy thunk, so the villain LANDS as a character rather than only a copy line. He
    // rides the pre-round layer (torn down on tap/auto-start). Motion-gated: reduced-motion places him
    // statically over the corner with no slam / no shake (a calm held presence).
    if (this.noviceRun && this.boardIndex === 2) {
      const size = Math.min(88, W * 0.2);
      const landX = W / 2 + plateW / 2 - size * 0.3;
      const landY = readyY - plateH * 0.5 - size * 0.15;
      const byt3 = attachMascot(this, 'rival', landX, landY, size, { bob: false, depth: 27_000 }).obj;
      layer.add(byt3);
      const baseScale = byt3.scale || 1;
      if (motionAllowed()) {
        byt3.setPosition(W + size, landY - size * 0.55).setScale(baseScale * 1.3).setAngle(16).setAlpha(0);
        this.tweens.add({
          targets: byt3,
          x: landX,
          y: landY,
          scale: baseScale,
          angle: 0,
          alpha: 1,
          duration: 360,
          ease: 'Back.easeOut',
          onComplete: () => {
            if (!byt3.active) return;
            shake(this.cameras.main, 8, 220);
            audio().playSfx('lock', { rate: 0.62, volume: 1 });
            this.time.delayedCall(70, () => audio().playSfx('hazardNear', { rate: 0.82, volume: 0.42 }));
            // A small landing squash that settles back — the "impact" follow-through on the slam.
            this.tweens.add({
              targets: byt3,
              scaleX: baseScale * 1.12,
              scaleY: baseScale * 0.9,
              duration: 90,
              yoyo: true,
              ease: 'Quad.easeOut',
            });
          },
        });
      }
    }
    // Auto-start fallback so the beat never blocks a player who doesn't tap; a touch starts instantly.
    // ~1s topic beat for normal play (a touch longer for first-time novice + warmup teaching).
    const holdMs = this.noviceRun ? 1700 : this.rivalWarmup ? 1400 : 1000;
    this.time.delayedCall(holdMs, () => this.startRound());
  }


export function startRound(this: GameScene): void {
    if (!this.preRoundActive) return;
    this.preRoundActive = false;
    audio().playSfx('uiConfirm');
    // BYT3 opens the match with trash talk on his JOIN board (first enabled board — board 1 after an
    // organic warmup, board 0 on a direct launch), ~30% on later boards — never on training. The ladder
    // flag gates the grudge caption off a ghost duel. Delayed past the round-start announcer line, which
    // playBark correctly yields to.
    if (!this.noviceRun) this.time.delayedCall(2300, () => this.rivalPersona?.reactBoardStart(this.ladderRace !== null));
    const layer = this.preRoundLayer;
    this.preRoundLayer = undefined;
    if (!layer) return;
    this.tweens.add({
      targets: layer,
      alpha: 0,
      duration: 140,
      ease: 'Quad.easeIn',
      onComplete: () => layer.destroy(),
    });
    this.scheduleEarlyScanHint();
  }


export function scheduleEarlyScanHint(this: GameScene): void {
    const shouldTeachScan = this.scanUnlocked && (this.boardIndex === 0 || (this.noviceRun && this.boardIndex === 1));
    if (this.taughtEarlyScan || !shouldTeachScan) return;
    this.taughtEarlyScan = true;
    const delayMs = this.noviceRun && this.boardIndex === 1 ? 120 : 1100;
    this.time.delayedCall(delayMs, () => {
      if (!this.scene.isActive() || this.ended || this.paused || this.preRoundActive || !this.scanButton?.active) return;
      const human = this.sim.snapshot().players.find((p) => p.id === PLAYER);
      if ((human?.scansLeft ?? 0) <= 0) return;
      // S9 FIX-C caption-band arbiter: the teach bubble CARRIES the message ("SCAN reveals this tile +
      // neighbors"), anchored at the actual button — the old duplicate toast-slot line said the same
      // words in a second place and (worse) held the slot, which suppressed the bubble it paired with
      // under the new one-lane rule. One voice: button pulse + anchored bubble only.
      this.pulseDockButton(this.scanButton, PALETTE.scan, 780);
      this.showScanTeachBubble();
    });
  }


export function showScanTeachBubble(this: GameScene): void {
    if (!this.scanButton?.active) return;
    // S9 FIX-C CAPTION-BAND ARBITER (G0 AMATEURs 4-7): the tooltip is the LOWEST caption lane. While a
    // transient coach caption holds the toast slot (LOCKED / teach beat) or the SKIPPED pill is live,
    // rendering it anyway is exactly the audit's double/triple stack — so it is SUPPRESSED: wait out the
    // blocker once and retry; blocked again → drop it entirely (the pulsing SCAN button still carries
    // the affordance, and the bubble re-arms on the next board via taughtEarlyScan).
    const blockedMs = Math.max(
      this.hintHoldUntilMs - this.time.now,
      this.underTextYieldUntilMs - this.time.now,
      this.safePassBanner?.active ? 900 : 0,
    );
    if (blockedMs > 0) {
      if (!this.scanBubbleRetried) {
        this.scanBubbleRetried = true;
        this.time.delayedCall(blockedMs + 220, () => {
          if (!this.scene.isActive() || this.ended || this.paused || this.preRoundActive || !this.scanButton?.active) return;
          this.showScanTeachBubble();
        });
      }
      return;
    }
    const W = this.scale.width;
    const land = isLandscape();
    // S9 FIX-C (G0 AMATEUR 5 — desktop mis-anchor): the portrait-era viewport-fraction clamp dragged the
    // bubble OFF the rail toward the board with its arrow aimed at the viewport edge under the CHOMP CTA.
    // In landscape the SCAN button lives in the right command rail — anchor the bubble directly ABOVE IT
    // (the actual scan affordance), arrow pointing down at the button. Portrait keeps the board-bottom
    // clamp (bubble must never sit over tiles) and its on-screen x clamp.
    const x = land ? this.scanButton.x : Phaser.Math.Clamp(this.scanButton.x, W * 0.23, W * 0.77);
    const y = land
      ? this.scanButton.y - 62
      : Math.max(this.gridY + this.cellH * this.rows + 14, this.scanButton.y - 62);
    const bubbleW = Math.min(W * 0.76, 286);
    const bubbleH = 44;
    const c = this.add.container(x, y).setDepth(9_200).setAlpha(0);
    const bg = this.add
      .rectangle(0, 0, bubbleW, bubbleH, PALETTE.surface, 0.96)
      .setStrokeStyle(2, PALETTE.scan, 0.88);
    const text = this.add
      .text(0, -2, 'SCAN reveals this tile + neighbors', {
        fontFamily: FONT.family,
        fontSize: `${Math.max(12, Math.min(15, W * 0.03))}px`,
        color: hex(PALETTE.white),
        fontStyle: 'bold',
        align: 'center',
        wordWrap: { width: bubbleW - 18, useAdvancedWrap: true },
      })
      .setOrigin(0.5)
      .setStroke('#06111f', 3);
    // Arrow points at the SCAN button even when the portrait x clamp moved the bubble centre off it.
    const arrowX = Phaser.Math.Clamp(this.scanButton.x - x, -bubbleW / 2 + 16, bubbleW / 2 - 16);
    const arrow = this.add.triangle(arrowX, bubbleH / 2 + 6, 0, 0, 14, 0, 7, 10, PALETTE.scan, 0.95);
    c.add([bg, text, arrow]);
    // Registered on the scene so a higher caption lane (coach line / SKIPPED pill) can retire it early.
    this.scanTeachBubble = c;
    c.once(Phaser.GameObjects.Events.DESTROY, () => {
      if (this.scanTeachBubble === c) this.scanTeachBubble = undefined;
    });
    this.tweens.add({
      targets: c,
      alpha: 1,
      y: y - 4,
      duration: 180,
      ease: 'Quad.easeOut',
    });
    this.time.delayedCall(3000, () => {
      if (!c.active) return;
      this.tweens.add({
        targets: c,
        alpha: 0,
        y: y - 12,
        duration: 220,
        ease: 'Quad.easeIn',
        onComplete: () => c.destroy(),
      });
    });
  }


  /**
   * S9 FIX-C caption-band arbiter: retire the SCAN tooltip IMMEDIATELY when a higher caption lane
   * (coach line via showCommandHint, or the SKIPPED status pill) claims the band. Fast fade — a
   * suppression, not a beat. No-op when no bubble is live.
   */
export function dismissScanTeachBubble(this: GameScene): void {
    const c = this.scanTeachBubble;
    this.scanTeachBubble = undefined;
    if (!c?.active) return;
    this.tweens.killTweensOf(c);
    this.tweens.add({ targets: c, alpha: 0, duration: 110, ease: 'Quad.easeIn', onComplete: () => c.destroy() });
  }
